import React, { useEffect, useState } from "react";
import { Box, CircularProgress, Collapse, Typography } from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import { fetchImageUrl, fetchPdfUrl } from "../gestionCarteraApi";
import { formatDocument, formatFullName } from "../gestionCarteraFormat";
import { COLORS, ModalShell, SecondaryButton, StatusBadge } from "../gestionCarteraUi";
import CreditInvoicePanel from "./CreditInvoicePanel";

/** Documentos a nivel cliente: mismas llaves S3 que la página "Documentación Clientes". */
const DOCUMENTS = [
  { section: "Identidad", key: "frontDocumentUuid", label: "Cédula · frente", kind: "image" },
  { section: "Identidad", key: "backDocumentUuid", label: "Cédula · reverso", kind: "image" },
  { section: "Identidad", key: "selfieUuid", label: "Foto del cliente", kind: "image" },
  { section: "Documentos", key: "pagareZapsignUuid", label: "Pagaré", kind: "pdf" },
  { section: "Documentos", key: "tusDatosUuid", label: "Informe TusDatos", kind: "pdf" },
  { section: "Documentos", key: "experianUuid", label: "Informe Experian", kind: "pdf" },
];

const INVOICE_KEY = "factura";

const isValidKey = (key) => key && key !== "null" && key !== "undefined";

const resolveUrl = async (document, client) => {
  const key = client?.[document.key];
  if (!isValidKey(key)) return null;
  try {
    return document.kind === "pdf" ? await fetchPdfUrl(key) : await fetchImageUrl(key);
  } catch (error) {
    return null;
  }
};

function IdentityPhoto({ document, url, loading }) {
  return (
    <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 1 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <ImageOutlinedIcon sx={{ color: COLORS.primary }} fontSize="small" />
        <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{document.label}</Typography>
      </Box>
      <Box
        sx={{
          minHeight: 220,
          border: `1px solid ${COLORS.border}`,
          borderRadius: "12px",
          backgroundColor: "#F8F9FB",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        {loading && <CircularProgress size={22} />}
        {!loading && url && (
          <Box
            component="img"
            src={url}
            alt={document.label}
            sx={{ width: "100%", maxHeight: 420, objectFit: "contain" }}
          />
        )}
        {!loading && !url && <StatusBadge tone="gray" label="No disponible" />}
      </Box>
    </Box>
  );
}

function DocumentPreview({ document, url, loading, expanded, onToggle }) {
  const canExpand = !loading && Boolean(url);
  return (
    <Box sx={{ border: `1px solid ${COLORS.border}`, borderRadius: "12px", overflow: "hidden", backgroundColor: "#fff" }}>
      <Box
        component="button"
        type="button"
        disabled={!canExpand}
        aria-expanded={canExpand ? expanded : undefined}
        onClick={onToggle}
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1.5,
          py: 1.25,
          border: 0,
          background: "none",
          cursor: canExpand ? "pointer" : "default",
          font: "inherit",
          textAlign: "left",
          color: COLORS.text,
        }}
      >
        <DescriptionOutlinedIcon sx={{ color: COLORS.primary }} fontSize="small" />
        <Typography sx={{ flex: 1, fontSize: 14, fontWeight: 500 }}>{document.label}</Typography>
        {loading && <CircularProgress size={18} />}
        {!loading && !url && <StatusBadge tone="gray" label="No disponible" />}
        {canExpand && (
          <ExpandMoreIcon
            fontSize="small"
            sx={{ color: COLORS.primary, transform: expanded ? "rotate(180deg)" : "none", transition: "transform .2s" }}
          />
        )}
      </Box>
      <Collapse in={canExpand && expanded} unmountOnExit>
        <Box sx={{ px: 1.5, pb: 1.5, backgroundColor: "#F8F9FB" }}>
          <Box component="iframe" src={url} title={document.label} sx={{ width: "100%", height: 520, border: 0 }} />
        </Box>
      </Collapse>
    </Box>
  );
}

/** Fila "Factura": se despliega el panel de factura por crédito (solo créditos APPROVED o COMPLETED). */
function InvoiceSection({ clientUuid, expanded, onToggle, onSaved }) {
  return (
    <Box sx={{ border: `1px solid ${COLORS.border}`, borderRadius: "12px", overflow: "hidden", backgroundColor: "#fff" }}>
      <Box
        component="button"
        type="button"
        aria-expanded={expanded}
        aria-controls="gc-factura"
        onClick={onToggle}
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1.5,
          py: 1.25,
          border: 0,
          background: "none",
          cursor: "pointer",
          font: "inherit",
          textAlign: "left",
          color: COLORS.text,
        }}
      >
        <DescriptionOutlinedIcon sx={{ color: COLORS.primary }} fontSize="small" />
        <Typography sx={{ flex: 1, fontSize: 14, fontWeight: 500 }}>Factura</Typography>
        <ExpandMoreIcon
          fontSize="small"
          sx={{ color: COLORS.primary, transform: expanded ? "rotate(180deg)" : "none", transition: "transform .2s" }}
        />
      </Box>
      <Collapse in={expanded} unmountOnExit id="gc-factura">
        <Box sx={{ px: 1.5, py: 1.5, backgroundColor: "#F8F9FB" }}>
          {clientUuid && <CreditInvoicePanel clientUuid={clientUuid} onSaved={onSaved} />}
        </Box>
      </Collapse>
    </Box>
  );
}

/** M4 · Documentos del cliente. */
export default function DocumentosModal({ open, client, onClose, onSaved }) {
  const [urls, setUrls] = useState({});
  const [loading, setLoading] = useState(false);
  const [expandedKey, setExpandedKey] = useState(null);

  useEffect(() => {
    if (!open) {
      setExpandedKey(null);
      return;
    }
    if (!client) return;
    let cancelled = false;
    setLoading(true);
    Promise.all(DOCUMENTS.map((document) => resolveUrl(document, client))).then((resolved) => {
      if (cancelled) return;
      setUrls(Object.fromEntries(DOCUMENTS.map((document, index) => [document.key, resolved[index]])));
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [open, client]);

  const sections = [...new Set(DOCUMENTS.map((document) => document.section))];

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title="Documentos"
      subtitle={client ? `${formatFullName(client)} · ${formatDocument(client)}` : ""}
      maxWidth="xl"
      actions={<SecondaryButton onClick={onClose}>Cerrar</SecondaryButton>}
    >
      {sections.map((section) => (
        <Box key={section}>
          <Typography
            component="h3"
            sx={{ fontSize: 14, fontWeight: 700, color: COLORS.muted, mb: 1.5 }}
          >
            {section}
          </Typography>
          {section === "Identidad" ? (
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" }, gap: 2 }}>
              {DOCUMENTS.filter((document) => document.section === section).map((document) => (
                <IdentityPhoto key={document.key} document={document} url={urls[document.key]} loading={loading} />
              ))}
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {DOCUMENTS.filter((document) => document.section === section).map((document) => (
                <DocumentPreview
                  key={document.key}
                  document={document}
                  url={urls[document.key]}
                  loading={loading}
                  expanded={expandedKey === document.key}
                  onToggle={() => setExpandedKey((current) => (current === document.key ? null : document.key))}
                />
              ))}
              <InvoiceSection
                clientUuid={client?.uuid}
                expanded={expandedKey === INVOICE_KEY}
                onToggle={() => setExpandedKey((current) => (current === INVOICE_KEY ? null : INVOICE_KEY))}
                onSaved={onSaved}
              />
            </Box>
          )}
        </Box>
      ))}
    </ModalShell>
  );
}
